'use client'

import React, { useEffect, useRef, useState } from 'react'
import { motion, AnimatePresence } from "framer-motion";
import Image from "next/image";
import { X } from 'lucide-react';
import { Send, Loader2, User, Bot, Mic, MicOff } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { Button } from "@/components/ui/button"; // Assuming Shadcn UI
import { ScrollArea } from "@/components/ui/scroll-area";
import UserIcon from '@/components/UserIcon';
import { Textarea } from '@/components/ui/textarea';
import CartaAILogoSMDark from '@/../public/images/Carta-ai_Logo_sm_dark.png';
import { cn } from '@/lib/utils';
import FilterEx from '@/lib/badWordsUtils';
import { ToastError } from '@/lib/sonnerUtils';
import CartaTokens from '@/../public/images/tokens_sm.png';
import { GetTokenCost } from "@/lib/tokensHandlers";
import HttpStatus from '@/lib/HttpStatus';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface Message {
  role: 'user' | 'agent';
  text: string;
}

interface IChatBot {
  isOpen: boolean,
  isLoading: boolean,
  isListening: boolean,
  messages: Message[],
  userPhoto: string,
  userLocation?: string,
  input: string,
  handleSend: (overrideInput?: string) => void,
  setIsOpen: (val: boolean) => void,
  setInput: (input: string) => void,
  toggleListening: () => void,
  isPage?: boolean
}

function ChatBotUi({ 
    isPage, isOpen, isLoading, isListening, 
    messages, input, userPhoto, 
    handleSend, setIsOpen, setInput, toggleListening 
  }: IChatBot) {

  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [ error, setError ] = useState<string | null>(null);
  const [ frChatTokensCost, setFrChatTokensCost ] = useState<number>(0);
  const [ srcChatTokensCost, setSrcChatTokensCost ] = useState<number>(0);
  const [open, setOpen] = useState(false);

  const handleIsProfane = (text: string): string => {
    const filter = new FilterEx();

    if(filter.isProfane(text)) {
      const filteredText = filter.cleanExclude(text);
      setError("The usage of profane, offensive and inappropriate words is not allowed!");
      ToastError("The usage of profane, offensive and inappropriate words is not allowed!");
      return filteredText;
    } else {
      setError(null);
      return text;
    }
  }

  useEffect(() => {
    async function getFrChatTokensCost() {
      try {
        const costResp = await GetTokenCost('CBOTFRD01');
  
        if(costResp.status != HttpStatus.OK) {
          throw new Error(costResp.error!);
        }
        
        setFrChatTokensCost(costResp.data);
      } catch(error) {
        setFrChatTokensCost(0);
      }
    }

    async function getSrcChatTokensCost() {
      try {
        const costResp = await GetTokenCost('CBOTSRCH01');
  
        if(costResp.status != HttpStatus.OK) {
          throw new Error(costResp.error!);
        }
        
        setSrcChatTokensCost(costResp.data);
      } catch(error) {
        setSrcChatTokensCost(0);
      }
    }

    getFrChatTokensCost();
    getSrcChatTokensCost();
  }, []);
  
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 10 }}
          transition={{ duration: 0.3 }}
          className={
            cn("mb-4 w-[calc(100%-30px)]",
              isPage ? "w-full"
              : "max-w-[450px] md:w-[450px]", 
              "h-[410px] md:h-[510px] bg-background border rounded-xl shadow-2xl overflow-hidden origin-bottom-right")
          }
        >
          <div 
            className="p-4 bg-primary text-primary-foreground 
              flex flex-row justify-between items-center">
            {/* <span className="font-semibold">Carta-AI Support</span> */}
            <div>
              <div className='flex flex-row'>
                <Image src={CartaAILogoSMDark} 
                    className='ml-2 h-6 w-20 md:h-6 md:w-20' alt='Carta-AI Logo at the header' />
                <span className='text-left ml-2 text-lg'>Assistant</span>
              </div>
              {
                !isPage ? 
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="hover:bg-primary-foreground/10 text-primary-foreground"
                    onClick={() => setIsOpen(false)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                : null
              }
            </div>
            <TooltipProvider>
              <Tooltip open={open} onOpenChange={setOpen}>
                <TooltipTrigger asChild>
                  <div 
                    className='flex flex-row justify-center items-center cursor-help bg-white rounded-[50%] p-2'
                    // Handle hover for desktop
                    onMouseEnter={() => setOpen(true)}
                    onMouseLeave={() => setOpen(false)}
                    // Handle click/tap for mobile
                    onClick={() => setOpen(!open)}
                  >
                    <Image src={CartaTokens} width={20} height={20} alt="Carta AI Tokens" />
                    <span className='text-black text-lg font-medium'>{" ?"}</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent className='bg-indigo-300 text-black text-lg'>
                  <p>Friendly Chats: {frChatTokensCost} token. Chats with search: {srcChatTokensCost} token.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {/* <div className='flex flex-col md:flex-row justify-center items-center gap-2'>
              <div className="flex flex-row justify-center items-center gap-1 text-sm">
                <span>{"Friendly "}</span><Image src={CartaTokens} width={20} height={20} alt="Carta AI Tokens" />
                <span className="ml-1">{frChatTokensCost} tokens</span><span>{", "}</span>
              </div>
              <div className="flex flex-row justify-center items-center gap-1 text-sm">
                <span>{"Searching "}</span><Image src={CartaTokens} width={20} height={20} alt="Carta AI Tokens" />
                <span className="ml-1">{srcChatTokensCost} tokens</span>
              </div>
            </div> */}
          </div>

          {/* YOUR BOT COMPONENT GOES HERE */}
          <div className="h-[calc(100%-64px)] overflow-y-auto p-4 flex flex-col">
            <div className="text-sm text-muted-foreground text-center flex flex-col h-full">
              <div className='pl-4 pr-4 mb-4 h-[85%]'>
                <ScrollArea className="z-0 h-full p-2 pt-4 pb-4 
                  bg-indigo-200 border-2 border-slate-200 rounded-lg 
                  shadow-[inset_4px_4px_10px_rgba(0,0,0,0.1),inset_-2px_-2px_4px_rgba(255,255,255,0.7)]">
                  <div className="flex flex-col gap-4">
                      {messages.length === 0 && (
                      <p className="text-center text-muted-foreground text-sm mt-20">
                          Ask me to schedule a reminder or check your calendar.
                      </p>
                      )}
                      {messages.map((m, i) => (
                      <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                          <div className={`flex gap-2 max-w-[90%] ${m.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
                            <div className={`rounded-full h-8 w-8 flex items-center justify-center border 
                              ${m.role === 'user' 
                                ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                                {m.role === 'user' 
                                  ? (userPhoto 
                                    ? <UserIcon userPicture={userPhoto} photoOnly={true} /> 
                                    : <User size={16} />) 
                                  : <Bot size={16} className='rounded-full h-6 w-8' />}
                            </div>
                            <div className={`p-3 rounded-2xl text-sm text-left
                              ${m.role === 'user' 
                                ? 'bg-indigo-500 text-primary-foreground rounded-tr-none shadow-sm' 
                                : 'bg-muted text-foreground rounded-tl-none shadow-sm'}`}>
                                <ReactMarkdown>
                                  {m.text}
                                </ReactMarkdown>
                            </div>
                          </div>
                      </div>
                      ))}
                      {isLoading && (
                      <div className="flex justify-start">
                          <div className="bg-muted p-3 rounded-2xl rounded-tl-none animate-pulse">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          </div>
                      </div>
                      )}
                      <div ref={scrollRef} />
                  </div>
                </ScrollArea>
              </div>

              <div className='pt-0 pl-4 pr-4 h-auto mb-4'>
                  <form onSubmit={(e) => { e.preventDefault(); handleSend(); }} className="flex w-full items-center space-x-2">
                  {/* ✅ Voice Command Button */}
                  <Textarea 
                      ref={textareaRef}
                      value={input} 
                      onChange={(e) => {
                        let message = handleIsProfane(e.currentTarget.value);
                        setInput(message);
                      }} 
                      onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                              e.preventDefault();
                              handleSend();
                          }
                      }}
                      placeholder={isListening ? "Listening..." : "Type your message..."}
                      className="min-h-[40px] max-h-[200px] flex-1 resize-none overflow-y-auto py-3 leading-relaxed"
                      rows={1}
                  />
                  <Button 
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={toggleListening}
                      className={`transition-all 
                        ${isListening 
                          ? 'text-red-500 scale-110 animate-pulse' 
                          : 'text-muted-foreground'}
                          border-2 border-gray-400`}
                  >
                      {isListening ? <MicOff size={20} color='blue' /> : <Mic size={20} color='blue' />}
                  </Button>
                  <Button type="submit" size="icon" disabled={isLoading || !input.trim()}>
                      <Send className="h-4 w-4" />
                  </Button>
                  </form>
              </div>
            </div>
            {/* <BotComponent /> */}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default ChatBotUi